/**
 * SPEC-AgentDetail-Revisions — TimerChip + ExtendButton (two-state collapse).
 *
 * Two non-expired states (per Frame 5 of the mockup):
 *   - healthy: purple chip, no ExtendButton.
 *   - warning: red pulsing chip, "— Expiring Soon" suffix, ExtendButton
 *              ("⏱ + Time") shows beside it.
 * Expired hides the chip entirely (the subscription provider no longer
 * pushes a released toast).
 *
 * The critical tier was removed. Warning now carries the full alarm —
 * the pulse animation that used to live on `case "critical"` now lives on
 * `case "warning"`.
 *
 * ExtendButton drops the highlighted/keep-working variant. Single label
 * "⏱ + Time", single style (outlined purple).
 */
import { useEffect } from "react";
import { computeTimerState, computeTimerLabel, TimerState } from "@/lib/agent-ui-helpers";
import { Starling } from "@/lib/starling-brand";
import { qaVar, tint } from "@/lib/qa-theme";

// SPEC-4 Q7 — theme variables (light values unchanged), so the chip follows dark mode.
const TOKENS = {
  purple: qaVar("purple"),
  purpleSub: tint(qaVar("purple"), 0x1f),
  purpleSubB: tint(qaVar("purple"), 0x2e),
  bad: qaVar("bad-text"),
  badBg: qaVar("bad-bg"),
  badBorder: tint(qaVar("bad"), 0x4d),
  card: qaVar("card"),
  sub: qaVar("sub"),
  muted: qaVar("text-3"),
  border: qaVar("border"),
} as const;

const STYLE_ID = "agent-timer-chip-pulse";

function ensurePulseKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes agentClaimPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(185,28,28,.4); }
      50%      { box-shadow: 0 0 0 5px rgba(185,28,28,0); }
    }
  `;
  document.head.appendChild(el);
}

export interface TimerChipProps {
  secondsRemaining: number | null;
}

export function TimerChip({ secondsRemaining }: TimerChipProps) {
  useEffect(ensurePulseKeyframes, []);
  const state = computeTimerState(secondsRemaining);
  const label = computeTimerLabel(secondsRemaining);
  const style = chipStyle(state);
  return (
    <span
      data-testid="agent-timer-chip"
      data-state={state}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "5px 9px",
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        ...style,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: "currentColor",
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
}

function chipStyle(state: TimerState): React.CSSProperties {
  switch (state) {
    case "warning":
      return {
        background: TOKENS.badBg,
        color: TOKENS.bad,
        border: `1px solid ${TOKENS.badBorder}`,
        animation: "agentClaimPulse 1.2s ease-in-out infinite",
      };
    case "expired":
      return {
        background: TOKENS.sub,
        color: TOKENS.muted,
        border: `1px solid ${TOKENS.border}`,
      };
    default:
      // healthy
      return {
        background: TOKENS.purpleSub,
        color: TOKENS.purple,
        border: `1px solid ${TOKENS.purpleSubB}`,
      };
  }
}

export interface ExtendButtonProps {
  onExtend: () => void;
  loading?: boolean;
  testId?: string;
}

export function ExtendButton({ onExtend, loading, testId }: ExtendButtonProps) {
  return (
    <button
      type="button"
      onClick={onExtend}
      disabled={loading}
      data-testid={testId ?? "agent-extend-button"}
      style={{
        background: TOKENS.card,
        color: TOKENS.purple,
        border: `1px solid ${TOKENS.purpleSubB}`,
        padding: "5px 9px",
        borderRadius: 6,
        fontFamily: Starling.fontBody,
        fontSize: 11,
        fontWeight: 700,
        cursor: loading ? "not-allowed" : "pointer",
        opacity: loading ? 0.5 : 1,
      }}
    >
      ⏱ + Time
    </button>
  );
}
